import type { DnsStatusInput, SslStatusInput } from "./types";

function resolutionModeLabel(dns: DnsStatusInput): string | null {
  if (dns.resolution_mode === "cloudflare_proxy" || dns.proxied_via_cloudflare === true) {
    return "Cloudflare Proxy";
  }
  if (dns.resolution_mode === "direct" || dns.proxied_via_cloudflare === false) {
    return "direct (DNS only)";
  }
  return null;
}

/**
 * Format DNS failure evidence from backend fields only.
 * Returns null when there is nothing concrete to show.
 */
export function formatDnsEvidence(dns: DnsStatusInput | null | undefined): string | null {
  if (!dns || dns.state !== "failed") return null;

  const parts: string[] = [];
  const mode = resolutionModeLabel(dns);
  const expected = (dns.expected_ip ?? "").trim();
  const resolved = (dns.resolved_ips ?? []).filter(Boolean);
  const proxied = mode === "Cloudflare Proxy";

  if (mode) {
    parts.push(`Resolution: ${mode}`);
  }

  if (proxied) {
    if (dns.https_reachable === false) {
      parts.push("HTTPS unreachable");
    }
    if (dns.tls_valid === false) {
      parts.push("TLS certificate invalid or hostname mismatch");
    }
    if (dns.application_responds === false) {
      parts.push("AI Secure application did not respond at /health");
    }
    if (resolved.length > 0) {
      parts.push(`resolved ${resolved.join(", ")} (Cloudflare edge — origin IP not compared)`);
    }
  } else if (expected || resolved.length > 0) {
    const expectedBit = expected ? `expected origin ${expected}` : "expected origin IP not configured";
    const resolvedBit =
      resolved.length > 0 ? `resolved ${resolved.join(", ")}` : "resolved none";
    parts.push(`${expectedBit}; ${resolvedBit}`);
  }

  const msg = (dns.last_message ?? "").trim();
  if (msg && !parts.some((p) => p.includes(msg))) {
    parts.push(msg);
  }

  if (!proxied && expected && resolved.length > 0 && dns.matched_expected === false) {
    parts.push(`Recommendation: point ${dns.hostname || "hostname"} A/AAAA to origin ${expected}`);
  } else if (!proxied && !expected && resolved.length === 0 && msg) {
    // message already carries recommendation when backend provides it
  } else if (!proxied && expected && resolved.length === 0) {
    parts.push(`Recommendation: create DNS for ${dns.hostname || "hostname"} → ${expected}`);
  } else if (proxied && dns.application_responds === false) {
    parts.push(
      `Recommendation: verify tenant API is running and reachable at https://${dns.hostname || "hostname"}/health`,
    );
  } else if (proxied && dns.tls_valid === false) {
    parts.push(`Recommendation: fix TLS for ${dns.hostname || "hostname"} at Cloudflare/origin`);
  }

  return parts.length > 0 ? parts.join(". ") : null;
}

/**
 * Format SSL failure / expiry evidence from backend fields only.
 */
export function formatSslEvidence(ssl: SslStatusInput | null | undefined): string | null {
  if (!ssl) return null;
  if (ssl.state !== "failed" && ssl.state !== "expiring_soon") return null;

  const parts: string[] = [];
  const host = (ssl.hostname ?? "").trim();
  const msg = (ssl.last_message ?? "").trim();

  if (host) parts.push(`Host ${host}`);
  if (ssl.issuer) parts.push(`issuer ${ssl.issuer}`);
  if (ssl.hostname_matches === false) {
    parts.push("certificate hostname does not match");
  }
  if (ssl.expires_at) {
    const days =
      ssl.days_remaining != null ? ` (${ssl.days_remaining} day(s) remaining)` : "";
    parts.push(`expires ${ssl.expires_at}${days}`);
  }
  if (msg) parts.push(msg);

  if (ssl.state === "failed" && host) {
    parts.push(`Recommendation: renew or reissue TLS for ${host}`);
  } else if (ssl.state === "expiring_soon" && host) {
    parts.push(`Recommendation: renew TLS for ${host} before expiry`);
  }

  return parts.length > 0 ? parts.join(". ") : null;
}

/** Prefer evidence block; fall back to last_message for onboarding detail. */
export function dnsDetailWithEvidence(dns: DnsStatusInput): string {
  const evidence = formatDnsEvidence(dns);
  if (evidence) return evidence;
  return (dns.last_message || "").trim() || "DNS test failed";
}

export function sslDetailWithEvidence(ssl: SslStatusInput): string {
  const evidence = formatSslEvidence(ssl);
  if (evidence) return evidence;
  return (ssl.last_message || "").trim() || "SSL test failed";
}
